import Alert from '@/common/components/atom/alert';
import Text from '@/common/components/atom/text';
import { splunkInstance } from '@/common/services/splunk';
import { MouseEvent, ReactNode, useEffect, useMemo, useRef } from 'react';
import BookingPageFooter from './BookingPageFooter';
import { LocalPrivateSpecialistsConfig } from './config';
import DemandLine from './DemandLine';
import { LocalSpecialistsEventType, buildLocalSpecialistsEvent } from './events';
import { LocalPrivateSpecialistsList } from './LocalPrivateSpecialists';

/** getFreeTurn code: the clinic's own booking system did not answer (Dr. Taheri at the 07:00 release). */
export const CLINIC_UNREACHABLE_STATUS = 29;

export const FULL_FALLBACK_MESSAGE = 'نوبت پزشک پر شده است.';

/**
 * Code 29, or the request itself failed (network, 429): the doctor is not known to be full, the clinic's system did
 * not answer under the load. Owner's wording (2026-10-28); no «try again in a few minutes» promise.
 */
export const UNAVAILABLE_NOW_MESSAGE = 'به دلیل مراجعه زیاد در حال حاضر ارتباط با سیستم نوبت‌دهی این مرکز برقرار نیست.';

export const freeTurnErrorMessage = (message?: string, status?: number) =>
  status === undefined || status === CLINIC_UNREACHABLE_STATUS ? UNAVAILABLE_NOW_MESSAGE : message?.trim() || FULL_FALLBACK_MESSAGE;

interface BookingErrorInlineProps {
  config: LocalPrivateSpecialistsConfig;
  ownSlug: string;
  /** getFreeTurn's own message. */
  message?: string;
  status?: number;
  centerId?: string;
  /** Below everything, outside the white cards (the clinic's support contact). */
  support?: ReactNode;
}

/**
 * Booking page, «انتخاب زمان نوبت» (config.inlineOnBookingError): getFreeTurn failed. Instead of the bottom sheet, in
 * the page: the reason in one red line and the demand line; the doctors bookable now in their own card under it;
 * the support contact last, on the page background. Splunk: placement `booking-error` (events.ts).
 */
export const BookingErrorInline = ({ config, ownSlug, message, status, centerId, support }: BookingErrorInlineProps) => {
  const shownMessage = freeTurnErrorMessage(message, status);
  // on every event of this surface, so the list's view / clicks can be split by the error the patient hit
  const eventFields = useMemo(
    () => ({
      error_status: status ?? 'none',
      ...(centerId && { center_id: centerId }),
      ...(config.demandLine && { demand_line: true }),
    }),
    [status, centerId, config.demandLine],
  );
  const track = (type: LocalSpecialistsEventType, fields?: Record<string, unknown>) =>
    splunkInstance('search').sendEvent(
      buildLocalSpecialistsEvent(type, { slug: ownSlug, placement: 'booking-error', config }, { ...eventFields, ...fields }),
    );

  // once per error shown (a new error after a step change remounts this block); the ref also holds through
  // StrictMode's dev double effect, like useViewEvent
  const shownSent = useRef(false);
  useEffect(() => {
    if (shownSent.current) return;
    shownSent.current = true;
    track('error-shown', { server_message: String(message ?? '').slice(0, 200), shown_message: shownMessage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSupportClick = (event: MouseEvent<HTMLDivElement>) => {
    const link = (event.target as HTMLElement).closest('a');
    if (link) track('click-support', { target: link.getAttribute('href') });
  };

  return (
    <div className="flex flex-col" data-testid="booking-error-inline" data-status={status ?? 'none'}>
      <div className="flex flex-col gap-2">
        <Alert severity="error" className="p-3 text-red-500">
          <Text className="text-sm font-medium leading-6">{shownMessage}</Text>
        </Alert>
        <DemandLine text={config.demandLine} />
      </div>
      <LocalPrivateSpecialistsList config={config} ownSlug={ownSlug} placement="booking-error" eventFields={eventFields} />
      {support && (
        <BookingPageFooter>
          <div onClickCapture={handleSupportClick}>{support}</div>
        </BookingPageFooter>
      )}
    </div>
  );
};

export default BookingErrorInline;
